import React, { useState, useEffect } from 'react';
import PostListDesktop, { PostListProps as PostListDesktopProps } from './Desktop/PostList';
import PostListMobile, { PostListProps as PostListMobileProps } from './Mobile/PostList';
import CounterListDesktop from './Desktop/CounterList';
import CounterListMobile, { CounterListProps as CounterListMobileProps } from './Mobile/CounterList';
import UploadCounterDesktop from './Desktop/UploadCounter';
import UploadCounterMobile from './Mobile/UploadCounter';
import GuildMemberDesktop from './Desktop/GuildMember';
import GuildMemberMobile from './Mobile/GuildMember';
import MonsterDesktop from './Desktop/Monster';
import MonsterMobile from './Mobile/Monster';
import StrategyDesktop from './Desktop/Strategy';
import StrategyMobile from './Mobile/Strategy';
import StatsTab from './StatsTab';
import ProfileModal from './Common/ProfileModal';
import IdResetModal from './Common/IdResetModal';
import Login from './Common/Login';
import SeasonSelect from './Common/SeasonSelect';
import GroupSelect from './Common/GroupSelect';
import MonsterSelect from './Desktop/MonsterSelect';
import { useUser } from '../hooks/useUser';
import { useMonsterMapping } from '../hooks/useMonsterMapping';
import { usePosts } from '../hooks/usePosts';
import { useCounter } from '../hooks/useCounter';
import CounterPost from './Desktop/CounterPost';
import CounterPostMobile from './Mobile/CounterPost';
import { useMonsterManagement } from '../hooks/useMonsterManagement';
import { useGuildMembers } from '../hooks/useGuildMembers';
import SiegeBattleList from './SiegeBattleList';
import Vacation from './Vacation';
import SpeedPage from './SpeedPage';
import RunePage from './RunePage';
import SimPage from './SimPage';
import SettingsPage from './SettingsPage';
import TierPage from './TierPage';
import NoticePage from './NoticePage';
import InstallAppButton from '../components/InstallAppButton';
import { canManageSiege } from '../utils/permission';
import { preloadRuneImages } from './RuneStar';
import Roster from './Roster';
import DeckPlan from './DeckPlan';
import { loadMonsterMapping } from '../utils/monsterMapping';
import { installBackGuard, useBackHandler } from '../hooks/useBackHandler';


/**
 * 왼쪽 서랍에 놓이는 탭들. **순서가 화면 순서다.**
 *
 * 예전에는 탭마다 40줄짜리 단추를 손으로 여덟 번 써 뒀다(같은 스타일이 여덟 벌). 하나를
 * 고치면 나머지가 어긋나서, 목록으로 두고 한 번만 그린다.
 */
const TABS: { key: 'battle' | 'admin' | 'guild' | 'counter' | 'strategy' | 'defense' | 'deckplan' | 'vacation' | 'roster' | 'speed' | 'rune' | 'sim' | 'settings' | 'notice' | 'tier'; label: string; sep?: boolean }[] = [
  // 순서·구분선은 사용자 지시(2026-10-09): 점령전 기록 / 길드 운영 / 도구
  { key: 'counter', label: '전적검색' },
  { key: 'battle', label: '전투기록' },
  { key: 'deckplan', label: '방덱' },
  { key: 'defense', label: '통계' },
  { key: 'strategy', label: '공략' },
  // 2026-10-27: 길마·부길마가 쓰는 공지 — 안 읽은 새 공지가 있으면 빨간 점(사용자 지시)
  { key: 'notice', label: '공지' },
  { key: 'vacation', label: '휴가', sep: true },
  { key: 'roster', label: '보유몹' },
  { key: 'guild', label: '길드원' },
  { key: 'speed', label: '공속계산기', sep: true },
  // 2026-09-22: 다듬는 동안 운영자에게만 보였는데, 이제 길드원 모두에게 연다(사용자 지시).
  // 열어도 남의 것이 새지 않는다 — 계정 JSON 은 각자 골라 자기 브라우저에서만 읽는다.
  { key: 'rune', label: '룬 옵티마이저' },
  // 2026-10-10: 공덱·방덱을 골라 전투 엔진으로 싸워 본다(사용자 지시)
  { key: 'sim', label: '시뮬레이션' },
  { key: 'admin', label: '몬스터' },
  // 2026-10-29: AI 가 평가한 점령전 5성·4성 공격·방어 티어 — 몬스터 탭 밑(사용자 지시)
  { key: 'tier', label: '티어표' },
  // 2026-10-13: 카톡 봇 설정 — 길마·부길마(사이트 운영자)만 보인다(사용자 지시, 서버도 막는다)
  { key: 'settings', label: '설정' }
];

/**
 * 오른쪽 위 프로필 단추.
 *
 * 탭이 가로 한 줄(데스크톱)이든 왼쪽 서랍(모바일)이든 같은 자리·같은 모양이라 한 벌만 둔다.
 */
const ProfileIcon: React.FC<{ user: any; onOpen: () => void }> = ({ user, onOpen }) => (
  <div
    onClick={onOpen}
    style={{
      width: 'clamp(34px, 7vw, 42px)',
      height: 'clamp(34px, 7vw, 42px)',
      flex: 'none',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      border: '2px solid rgba(102, 126, 234, 0.1)',
      transition: 'all 0.3s ease'
    }}
    title="프로필"
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.2), 0 4px 8px rgba(0, 0, 0, 0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05)';
    }}
  >
    <img loading="lazy"
      src={user.profileImg || '/images/default-profile.png'}
      alt="프로필"
      style={{
        width: 'clamp(26px, 5.5vw, 32px)',
        height: 'clamp(26px, 5.5vw, 32px)',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }}
      onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
    />
  </div>
);

const PageContainer = () => {
  // 모바일 여부 체크 함수
  const checkIsMobile = () => {
    // User Agent 체크
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUA = mobileRegex.test(userAgent);

    // 터치 지원 체크
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // 화면 크기 및 비율 체크
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isSmallScreen = screenWidth < 768;
    const isPortrait = screenHeight > screenWidth; // 세로가 더 긴 화면 (모바일 특성)
    const aspectRatio = screenWidth / screenHeight;
    const isMobileAspectRatio = aspectRatio < 1; // 세로가 긴 화면

    // // 디버깅용 로그
    // console.log('Mobile Detection:', {
    //   userAgent: userAgent,
    //   isMobileUA,
    //   hasTouchScreen,
    //   maxTouchPoints: navigator.maxTouchPoints,
    //   screenWidth,
    //   screenHeight,
    //   isSmallScreen,
    //   isPortrait,
    //   aspectRatio: aspectRatio.toFixed(2),
    //   isMobileAspectRatio,
    //   result: isMobileUA || isSmallScreen || (isPortrait && screenWidth < 1500)
    // });

    // 1. User Agent가 모바일이면 모바일
    // 2. 화면이 768px 미만이면 모바일
    // 3. 세로가 긴 화면이고 너비가 1500px 미만이면 모바일 (태블릿 세로모드 포함)
    return isMobileUA || isSmallScreen || (isPortrait && screenWidth < 1024);
  };

  const [isMobile, setIsMobile] = useState(checkIsMobile());
  const [showDebug, setShowDebug] = useState(false); // 디버그 정보 표시
  // 카톡 공지 링크(/notice/<id>)로 들어오면 공지 탭부터 — 어느 글인지는 NoticePage 가 주소에서 읽는다(사용자 2026-10-27)
  const [tab, setTab] = useState<'battle' | 'admin' | 'guild' | 'counter' | 'strategy' | 'defense' | 'deckplan' | 'vacation' | 'roster' | 'speed' | 'rune' | 'sim' | 'settings' | 'notice' | 'tier'>(() => (/^\/notice\/[a-f0-9]{24}$/.test(window.location.pathname) ? 'notice'
    // 티어표 카톡 공유 링크(/tier/<attack|defense>/<5|4>, 사용자 2026-10-31) — 티어표 탭으로 연다
    : /^\/tier\/(attack|defense)\/(5|4|3)$/.test(window.location.pathname) ? 'tier' : 'battle'));
  // 룬 옵티마이저 그림(틀·문양·룬돌, 합쳐 130KB 쯤)은 앱이 뜰 때 미리 받아 둔다 — 탭을 열 때 받으면 늦다
  useEffect(() => { preloadRuneImages(); }, []);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const { user, setUser } = useUser();
  // 시뮬레이션은 애플톡에게만 (사용자 지시 2026-10-12, 서버 `isSimViewer` 가 정한다 — 서버도 막는다)
  const visibleTabs = TABS.filter((t) => (t.key !== 'sim' || !!user?.simViewer) && (t.key !== 'settings' || canManageSiege(user)));
  const { monsterMapping, setMonsterMapping } = useMonsterMapping(tab);
  const monsterManagement = useMonsterManagement(monsterMapping, setMonsterMapping);
  const { posts, setPosts } = usePosts(tab);
  const counter = useCounter();
  const [profileOpen, setProfileOpen] = useState(false);
  /**
   * 안 읽은 새 공지 수 — 공지 탭·메뉴 단추에 빨간 점. 로그인하면 묻고 5분마다 다시 묻는다.
   * 공지 탭을 열면 NoticePage 가 읽음을 남기고 `swpvpdb:notice-seen` 을 보낸다.
   */
  const [noticeUnread, setNoticeUnread] = useState(0);
  useEffect(() => {
    if (!user?.uuid) { setNoticeUnread(0); return; }
    const ask = () => fetch('/api/notices/unread', { credentials: 'include' })
      .then((r) => r.json()).then((d) => setNoticeUnread(d?.count || 0)).catch(() => {});
    ask();
    const t = window.setInterval(ask, 5 * 60 * 1000);
    const seen = () => setNoticeUnread(0);
    window.addEventListener('swpvpdb:notice-seen', seen);
    return () => { window.clearInterval(t); window.removeEventListener('swpvpdb:notice-seen', seen); };
  }, [user?.uuid]);
  const RedDot = ({ style }: { style?: React.CSSProperties }) => (
    <span aria-label="새 공지" style={{ position: 'absolute', width: 8, height: 8, borderRadius: '50%', background: '#e53935', border: '1.5px solid #fff', ...style }} />
  );
  // 다른 화면의 [공략 작성하러 가기] — 공략 탭으로 간다(작성 창은 공략 탭이 연다)
  useEffect(() => {
    const on = () => setTab('strategy');
    window.addEventListener('swpvpdb:write-tactic', on);
    return () => window.removeEventListener('swpvpdb:write-tactic', on);
  }, []);
  // 전투기록 창·공략의 [내 룬으로 맞추기] — 룬 옵티마이저 탭으로 간다(룬작은 utils/fitHandoff 에 맡겨져 있고 RunePage 가 꺼낸다)
  useEffect(() => {
    const on = () => setTab('rune');
    window.addEventListener('swpvpdb:open-rune-fit', on);
    return () => window.removeEventListener('swpvpdb:open-rune-fit', on);
  }, []);
  // 전투기록 창의 [시뮬레이션] — 시뮬레이션 탭으로 간다(전투는 utils/simHandoff 에 맡겨져 있고 SimPage 가 꺼낸다)
  useEffect(() => {
    const on = () => setTab('sim');
    window.addEventListener('swpvpdb:open-sim', on);
    return () => window.removeEventListener('swpvpdb:open-sim', on);
  }, []);
  /** 왼쪽 탭 서랍이 열려 있나 */
  const [menuOpen, setMenuOpen] = useState(false);
  const guildMembers = useGuildMembers();
  const [showUpload, setShowUpload] = useState(false);

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 게시글(점령전 기록) 탭 상태/핸들러/로직
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());
  const [selectedPost, setSelectedPost] = useState<any>(null);
  /**
   * **전투기록 공유 링크(`/post/siege-<열쇠>`)로 들어오면 그 전투 창을 바로 연다**(사용자 2026-10-13).
   * 예전에는 24자리 게시글 번호만 알아봐서 홈만 떴다. 로그인 전이면 열쇠를 들고 있다가 로그인한 뒤에 연다.
   */
  const [linkedRef, setLinkedRef] = useState<string | null>(() => {
    const m = window.location.pathname.match(/^\/post\/siege-(\d+-\d+-\d+)$/);
    return m ? m[1] : null;
  });
  const [linkedBattle, setLinkedBattle] = useState<any>(null);
  const [battleStep, setBattleStep] = useState<'season' | 'group' | 'list'>('season'); // 전투기록 선택 단계
  const [selectedSeason, setSelectedSeason] = useState<number>(0); // 시즌은 DB에서 로드 후 설정
  const [selectedSiegeWar, setSelectedSiegeWar] = useState<string>('all'); // 점령전 필터
  const [selectedResult, setSelectedResult] = useState<string>('all'); // 승패 필터
  const [filteredPosts, setFilteredPosts] = useState<any[]>([]); // 필터링된 게시글
  const [displayedPosts, setDisplayedPosts] = useState<any[]>([]); // 화면에 표시되는 누적 게시글
  const [isLoading, setIsLoading] = useState(false); // 로딩 상태
  const [hasMore, setHasMore] = useState(true); // 더 불러올 게시글이 있는지

  const postsPerPage = 20;

  // 그룹 데이터 프리페치 캐시 (시즌별)
  const [prefetchedGroups, setPrefetchedGroups] = useState<{ season: number; groups: any[] } | null>(null);

  useEffect(() => {
    if (tab === 'admin' || tab === 'battle') {
      loadMonsterMapping()
        .then(data => setMonsterMapping(data));
    }
    // 탭 전환 시 열려있는 게시글 모달 닫기
    setSelectedPost(null);
    counter.setCounterModalOpen(null);
    // 전투기록 단계 초기화
    if (tab !== 'battle') {
      setBattleStep('season');
    }
  }, [tab]);

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.uuid) {
          setUser(data);
          // 로그인 후 URL 파라미터 확인
          checkUrlParams(data);
        } else {
          // 로그인하지 않은 경우에도 URL 파라미터 확인
          checkUrlParams(null);
        }
      })
      .finally(() => {
        setCheckingAuth(false);
      });
  }, []);

  // URL 파라미터로 게시글 직접 접근 처리
  const checkUrlParams = (currentUser: any) => {
    // 1. URL 쿼리 파라미터 확인 (?post=id)
    const urlParams = new URLSearchParams(window.location.search);
    let postId = urlParams.get('post');
    let tacticId = urlParams.get('tactic');

    // 2. URL 경로 확인 (/post/:id 또는 /tactic/:id)
    if (!postId && !tacticId) {
      const postPathMatch = window.location.pathname.match(/^\/post\/([a-f0-9]{24})$/);
      if (postPathMatch) {
        postId = postPathMatch[1];
      }

      const tacticPathMatch = window.location.pathname.match(/^\/tactic\/([a-f0-9]{24})$/);
      if (tacticPathMatch) {
        tacticId = tacticPathMatch[1];
      }
    }

    // 전투기록 게시글 처리
    if (postId) {
      // 게시글 ID로 게시글 찾기
      fetch(`/api/posts/posting/${postId}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data && data._id) {
            // 게시글이 존재하면 모달 열기
            setTab('battle'); // 점령전 탭으로 이동

            // 게시글의 시즌과 그룹 정보로 상태 설정
            if (data.season) {
              setSelectedSeason(data.season);
            }
            if (data.group) {
              setSelectedSiegeWar(data.group);
            }
            // battleStep을 'list'로 설정하여 게시글 목록 화면으로 이동
            setBattleStep('list');

            // 로그인 상태에 관계없이 게시글 모달 열기
            if (currentUser) {
              // 로그인한 경우 조회수 증가 처리
              handlePostClick(data);
            } else {
              // 로그인하지 않은 경우 조회수 증가 없이 모달만 열기
              setSelectedPost(data);
            }

            // URL을 루트 경로로 변경 (히스토리에 추가하지 않음)
            window.history.replaceState({}, document.title, '/');
          } else {
            alert('게시글을 찾을 수 없습니다.');
            // URL을 루트 경로로 변경
            window.history.replaceState({}, document.title, '/');
          }
        })
        .catch(() => {
          alert('게시글을 찾을 수 없습니다.');
          // URL을 루트 경로로 변경
          window.history.replaceState({}, document.title, '/');
        });
    }

    // 공략 게시글 처리
    if (tacticId) {
      // 공략 ID로 공략 찾기
      fetch(`/api/tactics/${tacticId}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data && data._id) {
            // 공략이 존재하면 모달 열기
            setTab('strategy'); // 공략 탭으로 이동

            // 로그인 상태에 관계없이 공략 모달 열기
            if (currentUser) {
              // 로그인한 경우 조회수 증가 처리 (공략에 조회수 증가 API가 있다면)
              fetch(`/api/tactics/view`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: data._id })
              }).then(res => res.json())
                .then(viewData => {
                  if (viewData && typeof viewData.views === 'number') {
                    setSelectedPost({ ...data, views: viewData.views });
                  } else {
                    setSelectedPost(data);
                  }
                })
                .catch(() => {
                  setSelectedPost(data);
                });
            } else {
              // 로그인하지 않은 경우 조회수 증가 없이 모달만 열기
              setSelectedPost(data);
            }

            // URL을 루트 경로로 변경 (히스토리에 추가하지 않음)
            window.history.replaceState({}, document.title, '/');
          } else {
            alert('공략을 찾을 수 없습니다.');
            // URL을 루트 경로로 변경
            window.history.replaceState({}, document.title, '/');
          }
        })
        .catch(() => {
          alert('공략을 찾을 수 없습니다.');
          // URL을 루트 경로로 변경
          window.history.replaceState({}, document.title, '/');
        });
    }
  };

  /**
   * **뒤로 가기** — 사이트 안에서 한 걸음씩 물린다(`hooks/useBackHandler`). 예전엔 열린 창 셋만 닫고 나머지는 사이트가 닫혔다.
   * 여기서는 앱 바깥쪽 것만: 다른 탭이면 전투기록 탭으로, 메뉴·프로필·업로드·공략 글 창은 닫기. 화면 안쪽 창과 단계는 각 화면이 등록한다.
   */
  useEffect(() => { installBackGuard(); }, []);
  useBackHandler(tab !== 'battle', () => setTab('battle'));
  useBackHandler(menuOpen, () => setMenuOpen(false));
  useBackHandler(!!selectedPost, () => setSelectedPost(null));
  useBackHandler(!!linkedBattle, () => setLinkedBattle(null));
  useEffect(() => {
    if (!user || !linkedRef) return;
    const ref = linkedRef;
    setLinkedRef(null);
    window.history.replaceState({}, document.title, '/');
    fetch(`/api/siege-battles/as-posts?ref=${encodeURIComponent(ref)}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        const found = data?.posts?.[0];
        if (found) setLinkedBattle(found);
        else alert('전투기록을 찾을 수 없습니다.');
      })
      .catch(() => alert('전투기록을 찾을 수 없습니다.'));
  }, [user, linkedRef]);
  useBackHandler(profileOpen, () => setProfileOpen(false));
  useBackHandler(showUpload, () => setShowUpload(false));
  useBackHandler(counter.counterModalOpen !== null, () => counter.setCounterModalOpen(null));

  // 시즌 데이터는 한 번만 로드
  useEffect(() => {
    if (tab === 'battle' && selectedSeason > 0 && battleStep === 'list') {
      // 이미 같은 시즌 데이터가 있으면 다시 로드하지 않음
      const currentSeasonPosts = posts.length > 0 && posts[0]?.season === selectedSeason;
      if (currentSeasonPosts) return;

      setIsLoading(true);
      setPage(1);
      setDisplayedPosts([]);
      setHasMore(true);
      fetch(`/api/posts?season=${selectedSeason}`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setPosts(data);
          }
        });
    }
  }, [tab, selectedSeason, battleStep]);

  // 필터 변경 시 이미 로드된 데이터에서 필터링만 수행 (네트워크 요청 없음)
  // posts, selectedSiegeWar, selectedResult, battleStep 변경 시 실행
  useEffect(() => {
    if (tab !== 'battle' || battleStep !== 'list') return;

    // 데이터가 아직 없으면 로딩 중 (시즌 로드 useEffect가 처리)
    if (posts.length === 0) return;

    setPage(1);
    let filtered = posts;
    // 점령전 필터
    if (selectedSiegeWar !== 'all') {
      filtered = filtered.filter((post: any) => post.group === selectedSiegeWar);
    }
    // 결과 필터
    if (selectedResult === 'my') {
      filtered = filtered.filter((post: any) => user && post.uuid === user.uuid);
    } else if (selectedResult !== 'all') {
      filtered = filtered.filter((post: any) => post.result === selectedResult);
    }
    setFilteredPosts(filtered);
    setDisplayedPosts(filtered.slice(0, postsPerPage));
    setHasMore(filtered.length > postsPerPage);
    setIsLoading(false);
  }, [posts, selectedSiegeWar, selectedResult, battleStep]);

  // 전투기록 탭으로 돌아올 때 초기화
  useEffect(() => {
    if (tab === 'battle') {
      setBattleStep('season');
      setSelectedSeason(0);
      setSelectedSiegeWar('all');
      setSelectedResult('all');
    }
  }, [tab]);

  // 무한 스크롤: 다음 페이지 로드
  const loadMorePosts = () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    const nextPage = page + 1;
    const startIndex = (nextPage - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    const newPosts = filteredPosts.slice(startIndex, endIndex);

    if (newPosts.length > 0) {
      setDisplayedPosts(prev => [...prev, ...newPosts]);
      setPage(nextPage);
      setHasMore(endIndex < filteredPosts.length);
      setIsLoading(false);
    } else {
      setHasMore(false);
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    setUser(null);
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname + ";";
  };

  // 서버에서 시즌 데이터 다시 로드 (업로드/삭제 등 데이터 변경 시 사용)
  const refreshPosts = async () => {
    if (selectedSeason === 0) return;
    try {
      const res = await fetch(`/api/posts?season=${selectedSeason}`, { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) {
        setPosts(data); // posts 변경 → useEffect에서 자동 필터링
      }
    } catch (e) {
      console.error('데이터 로드 실패:', e);
    }
  };

  const handleSearch = async () => {
    if (selectedSeason === 0) return;
    try {
      // searchValue가 비어있으면 전체 게시글 로드, 아니면 검색
      const url = searchValue
        ? `/api/posts?author=${encodeURIComponent(searchValue)}&season=${selectedSeason}`
        : `/api/posts?season=${selectedSeason}`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setExpandedPosts(new Set());
      setSelectedPost(null);
      setPosts(data); // posts 변경 → useEffect에서 자동 필터링
    } catch (e) {
      alert('검색 중 오류가 발생했습니다.');
    }
  };

  const handleSearchReset = async () => {
    if (selectedSeason === 0) return;
    setSearchValue('');
    try {
      const res = await fetch(`/api/posts?season=${selectedSeason}`, { credentials: 'include' });
      const data = await res.json();
      setExpandedPosts(new Set());
      setSelectedPost(null);
      setPosts(data); // posts 변경 → useEffect에서 자동 필터링
    } catch (e) {
      alert('초기화 중 오류가 발생했습니다.');
    }
  };

  const togglePost = (postId: string) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  const handlePostClick = async (post: any) => {
    let newViews = post.views || 0;
    try {
      const res = await fetch(`/api/posts/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: post._id })
      });
      const data = await res.json();
      if (data && typeof data.views === 'number') {
        newViews = data.views;
        setSelectedPost({ ...post, views: newViews });
        setPosts(prevPosts => prevPosts.map(p => p._id === post._id ? { ...p, views: newViews } : p));
      } else {
        setSelectedPost(post);
      }
    } catch {
      setSelectedPost(post);
    }
  };

  const handlePostDeleted = () => {
    // 게시글 삭제 후 목록 새로고침
    if (tab === 'battle') {
      // 삭제된 게시글을 즉시 로컬 상태에서 제거 (selectedPost가 아직 존재함)
      const deletedPostId = selectedPost?._id;
      if (deletedPostId) {
        // 즉시 로컬 상태 업데이트 (UI 즉시 반영)
        const newFilteredPosts = filteredPosts.filter((p: any) => p._id !== deletedPostId);
        const newDisplayedPosts = displayedPosts.filter((p: any) => p._id !== deletedPostId);
        setFilteredPosts(newFilteredPosts);
        setDisplayedPosts(newDisplayedPosts);
        setPosts(prev => prev.filter((p: any) => p._id !== deletedPostId));
      }

      // 백그라운드에서 서버 데이터와 동기화
      refreshPosts();
    }
    setSelectedPost(null);
  };

  if (!user) {
    return <Login checkingAuth={checkingAuth} onLogin={async () => {
      fetch('/api/auth/me', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.uuid) setUser(data);
        });
    }} />;
  }

  return (
    <>
      {/* 디버그 정보 표시 */}
      {showDebug && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          color: '#fff',
          padding: '10px',
          fontSize: '11px',
          zIndex: 9999,
          fontFamily: 'monospace',
          lineHeight: '1.5'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
            <strong>Mobile Detection Debug</strong>
            <button
              onClick={() => setShowDebug(false)}
              style={{
                background: '#f44336',
                color: '#fff',
                border: 'none',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '10px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
          <div>isMobile: <strong style={{ color: isMobile ? '#4caf50' : '#f44336' }}>{isMobile ? 'TRUE' : 'FALSE'}</strong></div>
          <div>Current Tab: <strong style={{ color: '#4caf50' }}>{tab.toUpperCase()}</strong></div>
          <div>Component: <strong style={{ color: '#4caf50' }}>{isMobile ? 'MOBILE' : 'DESKTOP'}</strong></div>
          <div>Screen: {window.innerWidth} x {window.innerHeight}</div>
          <div>Portrait: {window.innerHeight > window.innerWidth ? 'YES' : 'NO'}</div>
          <div>User Agent: {navigator.userAgent.substring(0, 40)}...</div>
          <div>Touch Points: {navigator.maxTouchPoints}</div>
        </div>
      )}
    <div className="App" style={{ minHeight: '100vh', background: '#f4f7fb', fontFamily: 'Pretendard, Noto Sans KR, sans-serif' }}>
      {/*
        탭 자리는 화면에 따라 다르다.

        데스크톱  위쪽 **가로 한 줄** — 폭이 넉넉해서 대개 다 보이고(좁으면 옆으로 민다), 한 번에 옮겨 갈 수
                  있는 것이 서랍보다 빠르다.
        모바일    **왼쪽 서랍** — 가로로 놓으면 스크롤이 생겨 뒤쪽 탭이 화면 밖에 숨는다.
                  세로라 몇 개가 되어도 다 보인다.
      */}
      {!isMobile && (
        // data-topbar — 다크모드에서 index.css 가 바탕·탭 글자를 따로 칠한다(밝기 뒤집기만으로는 거의 검정 + 흐린 회색 글자였다)
        <div data-topbar="" style={{
          display: 'flex',
          width: '100%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          height: 'clamp(44px, 8vh, 60px)',
          alignItems: 'center',
          padding: '0 clamp(8px, 2vw, 20px)',
          overflow: 'hidden',
          flexWrap: 'nowrap',
          minWidth: 0,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}>
          {/*
            탭이 열 개가 되면서 좁은 창에서는 한 줄에 다 안 들어간다. 바깥이 overflow:hidden
            이라 예전 설정(flex:none)으로는 뒤쪽 탭이 **그냥 잘려 사라졌다**. 안 들어갈 때는
            옆으로 밀어 볼 수 있게 한다(줄바꿈은 탭 줄 높이가 흔들려서 쓰지 않는다).
          */}
          <div style={{ display: 'flex', flex: '0 1 auto', minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {visibleTabs.map((t, i) => {
              const on = tab === t.key;
              return (
                <React.Fragment key={t.key}>
                  {/* 탭 사이 얇은 구분선 — 묶음이 바뀌는 자리(`sep`)는 진하고 넓게 */}
                  {i > 0 && (
                    <div style={{
                      width: t.sep ? 2 : 1,
                      height: t.sep ? 'clamp(24px, 4.5vh, 28px)' : 'clamp(20px, 4vh, 24px)',
                      background: t.sep
                        ? 'linear-gradient(180deg, transparent 0%, rgba(102, 126, 234, 0.45) 50%, transparent 100%)'
                        : 'linear-gradient(180deg, transparent 0%, rgba(102, 126, 234, 0.15) 50%, transparent 100%)',
                      margin: t.sep ? 'auto clamp(6px, 1vw, 10px)' : 'auto clamp(2px, 0.5vw, 4px)',
                      alignSelf: 'center'
                    }} />
                  )}
                  <button
                    onClick={() => setTab(t.key)}
                    data-tab-on={on ? '1' : '0'}
                    style={{
                      position: 'relative',
                      height: 'clamp(32px, 6vh, 40px)',
                      fontWeight: on ? 700 : 500,
                      padding: '0 clamp(8px, 2vw, 20px)',
                      border: 'none',
                      background: on ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                      color: on ? '#fff' : '#555',
                      fontSize: 'clamp(10px, 2.5vw, 12px)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      whiteSpace: 'nowrap',
                      boxShadow: on ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                      transform: on ? 'translateY(-1px)' : 'translateY(0)'
                    }}
                    onMouseEnter={(e) => {
                      if (on) return;
                      e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)';
                      e.currentTarget.style.color = '#667eea';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      if (on) return;
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#555';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {t.label}
                    {t.key === 'notice' && noticeUnread > 0 && !on && <RedDot style={{ top: 4, right: 4 }} />}
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          <div style={{ flex: 1 }} />

          {user && <ProfileIcon user={user} onOpen={() => setProfileOpen(true)} />}
        </div>
      )}

      {isMobile && (<>
      <div data-topbar="" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(8px, 2vw, 12px)',
        width: '100%',
        height: 'clamp(48px, 8vh, 56px)',
        padding: '0 clamp(10px, 2.5vw, 16px)',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 300,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)'
      }}>
        {/* 햄버거 — 누르면 서랍이 나오고, 다시 누르면 들어간다 */}
        <button
          onClick={() => setMenuOpen((v) => !v)}
          title="메뉴"
          aria-label="메뉴"
          style={{
            width: 'clamp(34px, 7vw, 40px)',
            height: 'clamp(34px, 7vw, 40px)',
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: 10,
            background: menuOpen ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
            cursor: 'pointer',
            padding: 0,
            position: 'relative'
          }}
        >
          {noticeUnread > 0 && !menuOpen && tab !== 'notice' && <RedDot style={{ top: -3, right: -3 }} />}
          {[0, 1, 2].map((i) => (
            <span key={i} style={{
              display: 'block',
              width: 18,
              height: 2,
              borderRadius: 2,
              background: menuOpen ? '#fff' : '#667eea'
            }} />
          ))}
        </button>

        {/* 지금 어느 탭인지 — 서랍이 닫혀 있어도 알 수 있어야 한다 */}
        <span style={{
          fontSize: 'clamp(14px, 3.4vw, 17px)',
          fontWeight: 800,
          letterSpacing: '-0.3px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          whiteSpace: 'nowrap'
        }}>
          {visibleTabs.find((t) => t.key === tab)?.label || '설탕길드'}
        </span>

        <div style={{ flex: 1 }} />

        {user && <ProfileIcon user={user} onOpen={() => setProfileOpen(true)} />}
      </div>

      {/* 서랍 바닥 — 누르면 닫힌다 */}
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 290,
            background: 'rgba(20, 24, 40, 0.35)'
          }}
        />
      )}

      {/* 서랍 */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: 'clamp(180px, 56vw, 240px)',
        zIndex: 310,
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow: menuOpen ? '4px 0 24px rgba(0, 0, 0, 0.18)' : 'none',
        // 닫혀 있을 때는 화면 밖에 둔다. `display:none` 대신 옮기면 여닫힘이 부드럽다
        transform: menuOpen ? 'translateX(0)' : 'translateX(-102%)',
        transition: 'transform 0.22s ease',
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 10px',
        gap: 4,
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 4px 10px', marginBottom: 4,
          borderBottom: '1px solid rgba(102, 126, 234, 0.12)'
        }}>
          <span style={{
            fontSize: 15, fontWeight: 900, letterSpacing: '-0.3px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
          }}>설탕길드</span>
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="닫기"
            style={{ border: 'none', background: 'none', fontSize: 20, lineHeight: 1, color: '#999', cursor: 'pointer' }}
          >×</button>
        </div>

        {visibleTabs.map((t) => {
          const on = tab === t.key;
          return (
            <React.Fragment key={t.key}>
            {/* 묶음이 바뀌는 자리 — 가로 구분선 */}
            {t.sep && <div style={{ height: 1, background: 'rgba(102, 126, 234, 0.18)', margin: '6px 8px' }} />}
            <button
              onClick={() => { setTab(t.key); setMenuOpen(false); }}
              style={{
                textAlign: 'left',
                padding: '10px 12px',
                border: 'none',
                borderRadius: 10,
                background: on ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: on ? '#fff' : '#555',
                fontSize: 13,
                fontWeight: on ? 800 : 500,
                cursor: 'pointer',
                boxShadow: on ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                transition: 'background 0.2s ease, color 0.2s ease'
              }}
              onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)'; }}
              onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
            >
              {t.label}
              {t.key === 'notice' && noticeUnread > 0 && !on && <RedDot style={{ position: 'relative', display: 'inline-block', marginLeft: 6, top: -6 }} />}
            </button>
            </React.Fragment>
          );
        })}
        {/* 맨 아래 — 홈 화면에 추가 (이미 앱으로 열렸으면 안 보인다) */}
        <InstallAppButton style={{ marginTop: 'auto' }} />
      </div>
      </>)}
      {/* 탭별 분기 - 모든 탭을 항상 렌더링하고 CSS로 숨김 처리 */}
      <div style={{ paddingTop: 1 }}>
        <div style={{ display: tab === 'battle' ? 'block' : 'none' }}>
          {/* 탭·소스가 실제로 선택됐을 때만 마운트한다.
              마운트 시점에 집계 API 를 부르므로 첫 화면을 무겁게 할 이유가 없다. */}
          {tab === 'battle' && (
            <SiegeBattleList
              isMobile={isMobile}
              monsterMapping={monsterMapping}
              user={user}
            />
          )}
        </div>

        <div style={{ display: tab === 'counter' ? 'block' : 'none' }}>
          {isMobile ? (
            <CounterListMobile {...counter} monsterMapping={monsterMapping} user={user} onPostClick={setSelectedPost} onPostDeleted={handlePostDeleted} />
          ) : (
            <CounterListDesktop {...counter} monsterMapping={monsterMapping} user={user} onPostClick={setSelectedPost} onPostDeleted={handlePostDeleted} />
          )}
        </div>

        <div style={{ display: tab === 'admin' ? 'block' : 'none' }}>
          {isMobile ? (
            <MonsterMobile {...monsterManagement} monsterMapping={monsterMapping} setMonsterMapping={setMonsterMapping} />
          ) : (
            <MonsterDesktop {...monsterManagement} monsterMapping={monsterMapping} setMonsterMapping={setMonsterMapping} />
          )}
        </div>

        <div style={{ display: tab === 'guild' ? 'block' : 'none' }}>
          {isMobile ? (
            <GuildMemberMobile />
          ) : (
            <GuildMemberDesktop />
          )}
        </div>

        <div style={{ display: tab === 'strategy' ? 'block' : 'none' }}>
          {isMobile ? (
            <StrategyMobile />
          ) : (
            <StrategyDesktop />
          )}
        </div>

        <div style={{ display: tab === 'deckplan' ? 'block' : 'none' }}>
          <DeckPlan isMobile={isMobile} user={user} />
        </div>

        <div style={{ display: tab === 'defense' ? 'block' : 'none' }}>
          {/* 공덱·방덱·길드원 고르기는 StatsTab 이 맡는다 */}
          <StatsTab monsterMapping={monsterMapping} user={user} isMobile={isMobile} />
        </div>

        <div style={{ display: tab === 'vacation' ? 'block' : 'none' }}>
          {/* 고른 뒤에 붙인다 — 달력은 열 때 그 달을 불러온다 */}
          {tab === 'vacation' && <Vacation isMobile={isMobile} />}
        </div>

        <div style={{ display: tab === 'tier' ? 'block' : 'none' }}>
          {/* 고른 뒤에 붙인다 — 열 때 서버에서 티어를 받아 온다 */}
          {tab === 'tier' && <TierPage isMobile={isMobile} />}
        </div>

        <div style={{ display: tab === 'speed' ? 'block' : 'none' }}>
          {/* 계산기라 받아올 것이 없다. 그래도 고른 뒤에 붙여 첫 화면을 가볍게 둔다 */}
          {tab === 'speed' && <SpeedPage isMobile={isMobile} monsterMapping={monsterMapping} />}
        </div>

        <div style={{ display: tab === 'rune' ? 'block' : 'none' }}>
          {/* 계정 JSON 을 브라우저에서만 읽어 셈한다 — 서버에서 받아올 것이 없다 */}
          {tab === 'rune' && <RunePage isMobile={isMobile} monsterMapping={monsterMapping} />}
        </div>

        <div style={{ display: tab === 'notice' ? 'block' : 'none' }}>
          {/* 열 때마다 새로 불러오고 읽음을 남긴다 */}
          {tab === 'notice' && <NoticePage isMobile={isMobile} />}
        </div>

        <div style={{ display: tab === 'settings' ? 'block' : 'none' }}>
          {tab === 'settings' && canManageSiege(user) && <SettingsPage isMobile={isMobile} />}
        </div>

        <div style={{ display: tab === 'sim' ? 'block' : 'none' }}>
          {/* 엔진은 화면에서 돈다 — 고른 뒤에 붙인다 */}
          {tab === 'sim' && !!user?.simViewer && <SimPage isMobile={isMobile} monsterMapping={monsterMapping} />}
        </div>

        <div style={{ display: tab === 'roster' ? 'block' : 'none' }}>
          {/* 고른 뒤에 붙인다 — 몹 표(84KB)와 보유 현황을 열 때 받는다 */}
          {tab === 'roster' && <Roster isMobile={isMobile} user={user} />}
        </div>

      </div>
      {/* 운영자가 아이디를 초기화했으면 들어오자마자 새 아이디를 정하게 한다 (mustChangeId — 바꿀 때까지 닫히지 않는다) */}
      {user?.mustChangeId && (
        <IdResetModal
          onDone={(id) => setUser((u: any) => u ? { ...u, userId: id, mustChangeId: false } : u)}
          onLogout={handleLogout}
        />
      )}
      {/* 프로필 모달도 분기 */}
      {profileOpen && user && (
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          uuid={user.uuid}
          nickname={user.nickname}
          setNickname={(nick: string) => setUser((u: any) => u ? { ...u, nickname: nick } : u)}
          userId={user.userId}
          setUserId={(id: string) => setUser((u: any) => u ? { ...u, userId: id } : u)}
          profileImg={user.profileImg}
          setProfileImg={(img: string | null) => setUser((u: any) => u ? { ...u, profileImg: img || '' } : u)}
          onLogout={handleLogout}
          onNavigate={(to) => {
            /**
             * 내 전투기록 — 길드원 탭의 "그 사람 기록" 창을 내 이름으로.
             * 길드원 통계 — 통계 탭의 [길드원 통계] 를 내 이름이 골라진 채로(사용자 지시: 통계 탭 안의 본인 통계).
             * 두 화면 모두 늘 그려 두므로 이미 떠서 듣고 있다.
             */
            const detail = { nickname: user.nickname };
            if (to === 'records') {
              setTab('guild');
              setTimeout(() => window.dispatchEvent(new CustomEvent('swpvpdb:open-records', { detail })), 0);
            } else {
              setTab('defense');
              setTimeout(() => window.dispatchEvent(new CustomEvent('swpvpdb:open-my-stats', { detail })), 0);
            }
          }}
        />
      )}

      {showUpload && (
        isMobile ? (
          <UploadCounterMobile
            onUpload={() => {
              setShowUpload(false);
              refreshPosts();
            }}
            onClose={() => setShowUpload(false)}
            author={user.nickname}
          />
        ) : (
          <UploadCounterDesktop
            onUpload={() => {
              setShowUpload(false);
              refreshPosts();
            }}
            onClose={() => setShowUpload(false)}
            author={user.nickname}
          />
        )
      )}

      {linkedBattle && (isMobile ? (
        <CounterPostMobile open post={linkedBattle} onClose={() => setLinkedBattle(null)} monsterMapping={monsterMapping} user={user} />
      ) : (
        <CounterPost open post={linkedBattle} onClose={() => setLinkedBattle(null)} monsterMapping={monsterMapping} user={user} />
      ))}
    </div>
    </>
  );
};

export default PageContainer; 
